import { useRef, type ReactNode } from 'react';
import { motion, useInView, useReducedMotion } from 'motion/react';
import { eases } from '@/shared/constants/motion.constants';

export interface ScrollIntoViewProps {
  children: ReactNode;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Custom className */
  className?: string;
  /** Animation variant */
  variant?: 'fade' | 'slideUp' | 'slideDown';
}

const VARIANTS = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  },
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  },
  slideDown: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
  },
};

/**
 * Animates children when they scroll into view.
 * Uses IntersectionObserver (via motion's useInView) to detect visibility.
 * Respects prefers-reduced-motion accessibility setting.
 *
 * Animation triggers once when 20% of the element becomes visible and never repeats.
 *
 * @example
 * <ScrollIntoView delay={0.1} variant="slideUp">
 *   <Card>Content here</Card>
 * </ScrollIntoView>
 */
export function ScrollIntoView({
  children,
  delay = 0,
  className,
  variant = 'slideUp',
}: ScrollIntoViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  const { initial, animate } = VARIANTS[variant];

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={initial}
      animate={isInView ? animate : initial}
      transition={{
        duration: 0.4,
        ease: eases.quartOut,
        delay,
      }}
    >
      {children}
    </motion.div>
  );
}

ScrollIntoView.displayName = 'ScrollIntoView';
